import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import {
  enrollment_status,
  enrollmentid,
  enrollmentrequest_status,
  enrollmentrequestid,
  enrollmentrequestprivateid,
  formula_type,
  internalteacherid,
  lessongroupid,
  reenrollmentid,
  studentid,
  teacherid,
  weekday,
} from "../../../sql/classy/types";
import * as planningstyle from "../../../styles/planning.css.classes";
import * as style from "../../../styles/style.css.classes";
import { bluePlanningColors } from "../../lib/colors";
import {
  parseEnrollmentRequestStatus,
  parseFormulaType,
  parseWeekday,
} from "../../parsing_from_ur";
import {
  HHmm,
  ddMMMMyyyy,
  renderEnrollmentrequestStatus,
  renderFirstNameLastName,
  renderFormulaTypeShort,
} from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";

type reenrollment = {
  id: reenrollmentid;

  no_reenrollment_reason: string | null;

  enr_req: null | {
    id: enrollmentrequestid;
    status: enrollmentrequest_status;
    student: {
      id: studentid;
      firstname: string;
      lastname: string;
    };
    reserveduntil: joda.LocalDate | null;
    showonplanninguntil: joda.LocalDate | null;
    private: {
      id: enrollmentrequestprivateid | null;
      time: joda.LocalTime | null;
      weekday: weekday | null;
      firstlesson: joda.LocalDate | null;
      teacher: {
        internalteacherid: internalteacherid | null;
        firstname: string | null;
        lastname: string | null;
      };
      instrument_description: string | null;
    };
    group: {
      id: lessongroupid | null;
      time: joda.LocalTime | null;
      weekday: weekday | null;
      instrument_description: string | null;
      teacher: {
        internalteacherid: internalteacherid | null;
        firstname: string | null;
        lastname: string | null;
      };
    };
  };
  enr: {
    id: enrollmentid;
    status: enrollment_status;
    needs_extra_approval: boolean;
    student: {
      id: studentid;
      firstname: string;
      lastname: string;
    };
    lessongroup: {
      id: lessongroupid;
      time: joda.LocalTime;
      firstlesson: joda.LocalDate | null;
      weekday: weekday;
      teacher: {
        id: teacherid;
        internalteacherid: internalteacherid;
        firstname: string;
        lastname: string;
      };
      instrument: {
        description: string;
      };
      type: formula_type;
    };
  } | null;
};

type batch = {
  stamp: joda.Instant;
  group: reenrollment[];
};

export function render(opts: {
  batches: batch[];
}): HTMLElement | HTMLElement[] {
  return opts.batches.map((batch) => {
    if (batch.group.length === 0) {
      return <span></span>;
    }

    const datetime = batch.stamp.atZone(joda.ZoneId.SYSTEM);

    return (
      <section className={style.panel}>
        <div
          className={style.panel_header}
          style={`background: var(--grey-900); color: var(--grey-100);`}
        >
          <b>
            {"Aangemaakt" +
              ": " +
              ddMMMMyyyy(datetime.toLocalDate()) +
              " - " +
              datetime.format(HHmm)}
          </b>
        </div>
        <table style="margin-top: -16px; margin-bottom: 0px;">
          {batch.group.map((reenr, i) =>
            reenr.enr_req
              ? renderEnrollmentrequest(reenr.enr_req, {
                  removeTopBorder: i === 0,
                  removeBottomBorder: i === batch.group.length - 1,
                })
              : reenr.enr
              ? renderEnrollment(reenr.enr, {
                  removeTopBorder: i === 0,
                  removeBottomBorder: i === batch.group.length - 1,
                })
              : reenr.no_reenrollment_reason
              ? []
              : [],
          )}
        </table>
      </section>
    );
  });
}

function renderEnrollmentrequest(
  enr_req: NonNullable<reenrollment["enr_req"]>,
  opts: { removeTopBorder: boolean; removeBottomBorder: boolean },
): HTMLElement {
  const studentLink = (
    <a
      href={routes.studentOverviewRoute.link({ studentid: enr_req.student.id })}
    >
      {renderFirstNameLastName(enr_req.student)}
    </a>
  );

  const privateLink =
    enr_req.private.time &&
    enr_req.private.firstlesson &&
    enr_req.private.teacher.internalteacherid ? (
      <a
        href={routes.planningRoute.link({
          time: enr_req.private.time,
          teacher: enr_req.private.teacher.internalteacherid,
          date: enr_req.private.firstlesson,
        })}
      >
        {"Privé" + ": "}
        {renderEnrollmentAsText(enr_req.private) || ""}
      </a>
    ) : null;

  const groupLink = enr_req.group.id ? (
    <a
      href={routes.lessongroup_generalRoute.link({
        lessongroupid: enr_req.group.id,
      })}
    >
      {"Groepsles" + ": "}
      {renderEnrollmentAsText(enr_req.group) || ""}
    </a>
  ) : null;

  const renderedStatus = renderEnrollmentrequestStatus(
    parseEnrollmentRequestStatus(enr_req.status),
  );
  const st = `background-color: ${renderedStatus[1].primary}; margin-right: 8px;`;
  const indicator = (
    <div className={planningstyle.lesson_status} style={st}>
      &nbsp;
    </div>
  );
  const detailsLink = (
    <div style="display: flex;">
      {indicator}
      <a
        href={routes.enrollment_requestRoute.link({
          enrollmentrequestid: enr_req.id,
        })}
      >
        {"Inschrijvingsaanvraag" + " - " + renderedStatus[0]}
      </a>
    </div>
  );

  const sty =
    (opts.removeTopBorder ? "border-top: 0px;" : "") +
    (opts.removeBottomBorder ? "border-bottom: 0px;" : "");

  return (
    <tr>
      <td style={sty}>{studentLink}</td>
      <td style={sty}>{privateLink || groupLink || "?"}</td>
      <td style={sty}>{detailsLink}</td>
    </tr>
  );
}

function renderEnrollmentAsText(enr_req: {
  time: joda.LocalTime | null;
  weekday: weekday | null;
  teacher: {
    internalteacherid: internalteacherid | null;
    firstname: string | null;
    lastname: string | null;
  };
  instrument_description: string | null;
}) {
  return enr_req.instrument_description &&
    enr_req.time &&
    enr_req.weekday &&
    enr_req.teacher.internalteacherid &&
    enr_req.teacher.firstname &&
    enr_req.teacher.lastname
    ? "bij %teachername, %instrumentname op %weekday om %time"
        .replace(
          "%teachername",
          renderFirstNameLastName({
            firstname: enr_req.teacher.firstname,
            lastname: enr_req.teacher.lastname,
          }),
        )
        .replace("%instrumentname", enr_req.instrument_description)
        .replace("%weekday", translateWeekday(parseWeekday(enr_req.weekday)))
        .replace("%time", enr_req.time.format(HHmm))
    : null;
}

function renderEnrollment(
  enr: NonNullable<reenrollment["enr"]>,
  opts: { removeTopBorder: boolean; removeBottomBorder: boolean },
): HTMLElement {
  const studentLink = (
    <a href={routes.studentOverviewRoute.link({ studentid: enr.student.id })}>
      {renderFirstNameLastName(enr.student)}
    </a>
  );

  const link = (
    <a
      href={routes.planningRoute.link({
        time: enr.lessongroup.time,
        teacher: enr.lessongroup.teacher.internalteacherid,
        date: enr.lessongroup.firstlesson || joda.LocalDate.now(),
      })}
    >
      {renderFormulaTypeShort(parseFormulaType(enr.lessongroup.type)) + ": "}
      {renderEnrollmentAsText({
        time: enr.lessongroup.time,
        weekday: enr.lessongroup.weekday,
        teacher: enr.lessongroup.teacher,
        instrument_description: enr.lessongroup.instrument.description,
      }) || ""}
    </a>
  );

  const st = `background-color: ${bluePlanningColors.primary}; margin-right: 8px;`;
  const indicator = (
    <div className={planningstyle.lesson_status} style={st}>
      &nbsp;
    </div>
  );
  const detailsLink = (
    <div style="display: flex;">
      {indicator}
      <a
        href={routes.lessongroup_generalRoute.link({
          lessongroupid: enr.lessongroup.id,
        })}
      >
        {"Inschrijving"}
      </a>
    </div>
  );

  const sty =
    (opts.removeTopBorder ? "border-top: 0px;" : "") +
    (opts.removeBottomBorder ? "border-bottom: 0px;" : "");

  return (
    <tr>
      <td style={sty}>{studentLink}</td>
      <td style={sty}>{link}</td>
      <td style={sty}>{detailsLink}</td>
    </tr>
  );
}
